import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { RotateCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { describeFileRead } from '../lib/fileReadability';
import { ExcelIcon, PdfIcon, PowerPointIcon, WordIcon } from './icons/file-icons';
import { GenericFileIcon } from './icons/file-icons-legacy';
import { JobStatus } from './JobStatus';
// Helper to get a descriptive name for the file type
const getFileTypeDescription = (file)=>{
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (file.type.startsWith('image/')) return 'Image';
    if (file.type.startsWith('video/')) return 'Video';
    if (file.type.startsWith('audio/')) return 'Audio';
    switch(extension){
        case 'pdf':
            return 'PDF document';
        case 'doc':
        case 'docx':
            return 'Word document';
        case 'xls':
        case 'xlsx':
            return 'Excel spreadsheet';
        case 'csv':
            return 'CSV file';
        case 'ppt':
        case 'pptx':
            return 'PowerPoint presentation';
        case 'txt':
            return 'Text file';
        case 'zip':
        case 'rar':
        case '7z':
            return 'Archive file';
        case 'ttf':
        case 'otf':
            return 'Font file';
        case 'json':
            return 'JSON file';
        case 'js':
        case 'jsx':
        case 'ts':
        case 'tsx':
            return 'TypeScript/JavaScript file';
        case 'html':
            return 'HTML file';
        case 'css':
            return 'CSS file';
        default:
            return 'File';
    }
};
// Helper to format file size
const formatFileSize = (bytes)=>{
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = [
        'Bytes',
        'KB',
        'MB',
        'GB',
        'TB'
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};
// Image Preview Component
const BlurredImagePreview = ({ file })=>{
    const [previewUrl, setPreviewUrl] = useState(null);
    useEffect(()=>{
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        return ()=>URL.revokeObjectURL(objectUrl);
    }, [
        file
    ]);
    if (!previewUrl) return /*#__PURE__*/ _jsx("div", {
        className: "w-full h-full bg-muted"
    });
    return /*#__PURE__*/ _jsx("div", {
        className: "w-full h-full bg-cover bg-center filter blur-sm",
        style: {
            backgroundImage: `url(${previewUrl})`
        }
    });
};
// An unpacked zip member chat cannot convert is still attached and can be
// filed; it never blocks send (a loose unreadable file never does either).
export const isUnreadableArchiveChild = ({ sourceArchiveName, jobId, status, processingStatus })=>Boolean(sourceArchiveName) && Boolean(jobId) && status === 'uploaded' && processingStatus === 'error';
// Aggregate row state for tests + accessibility. `processingStatus` is the
// load-bearing signal once an upload completes; before that, fall back to
// the upload status. This is the value the composer's submit-gate uses too.
export const attachmentAggregateState = ({ status, processingStatus, unreadableArchiveChild })=>unreadableArchiveChild ? 'ready' : status === 'error' || processingStatus === 'error' ? 'error' : status === 'uploading' || status === 'expanding' ? 'uploading' : processingStatus === 'processing' || processingStatus === 'queued' ? 'processing' : status === 'uploaded' && (processingStatus === 'ready' || !processingStatus) ? 'ready' : 'pending';
// "Retry-eligible" is either an upload that errored before a jobId existed
// (status === 'error') or a job that ran but failed during processing
// (processingStatus === 'error'). Both reuse the same cached File blob, so
// the composer's onRetry handler can re-drive uploadAttachmentViaTesoro
// without any backend requeue endpoint.
export const canRetryAttachment = ({ disabled, hasRetry, rejection, sourceArchiveName, jobId, status, processingStatus })=>!disabled && hasRetry && !rejection && !(sourceArchiveName && jobId && status === 'uploaded' && processingStatus === 'error') && (status === 'error' || processingStatus === 'error');
export const FileIcon = ({ file })=>{
    if (file.type.startsWith('image/')) {
        return /*#__PURE__*/ _jsx(BlurredImagePreview, {
            file: file
        });
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    const iconProps = {
        className: 'w-full h-full p-1.5'
    };
    switch(extension){
        case 'pdf':
            return /*#__PURE__*/ _jsx(PdfIcon, {
                ...iconProps
            });
        case 'doc':
        case 'docx':
            return /*#__PURE__*/ _jsx(WordIcon, {
                ...iconProps
            });
        case 'xls':
        case 'xlsx':
        case 'csv':
            return /*#__PURE__*/ _jsx(ExcelIcon, {
                ...iconProps
            });
        case 'ppt':
        case 'pptx':
            return /*#__PURE__*/ _jsx(PowerPointIcon, {
                ...iconProps
            });
        default:
            return /*#__PURE__*/ _jsx(GenericFileIcon, {
                ...iconProps
            });
    }
};
// Main File Attachment Component
export const FileAttachment = ({ file, onRemove, progress, status, jobId, processingStatus, disabled = false, statusLabel, rejection, sourceArchiveName, useDeepProcessing = false, onToggleDeepProcessing, outputFormat, onOutputFormatChange, onRetry, onJobTerminal })=>{
    const canRetry = canRetryAttachment({
        disabled,
        hasRetry: Boolean(onRetry),
        rejection,
        sourceArchiveName,
        jobId,
        status,
        processingStatus
    });
    const unreadableArchiveChild = isUnreadableArchiveChild({
        sourceArchiveName,
        jobId,
        status,
        processingStatus
    });
    const aggregateState = attachmentAggregateState({
        status,
        processingStatus,
        unreadableArchiveChild
    });
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "chat-attachment",
        "data-attachment-state": aggregateState,
        className: "bg-muted/50 border rounded-lg p-2.5 flex items-center justify-between gap-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "bg-white rounded-md w-9 h-9 flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden",
                        children: /*#__PURE__*/ _jsx(FileIcon, {
                            file: file
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm overflow-hidden",
                        children: [
                            /*#__PURE__*/ _jsx(TooltipProvider, {
                                delayDuration: 200,
                                children: /*#__PURE__*/ _jsxs(Tooltip, {
                                    children: [
                                        /*#__PURE__*/ _jsx(TooltipTrigger, {
                                            asChild: true,
                                            children: /*#__PURE__*/ _jsx("p", {
                                                className: "font-medium truncate text-foreground",
                                                children: file.name
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(TooltipContent, {
                                            children: file.name
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    getFileTypeDescription(file),
                                    " · ",
                                    formatFileSize(file.size)
                                ]
                            }),
                            !rejection && /*#__PURE__*/ _jsx("p", {
                                "data-testid": Testids.assistant.chatAttachmentReadability,
                                // specra-allow text-px: matches the existing text-[11px] captions in this file
                                className: "text-[11px] text-muted-foreground",
                                children: describeFileRead(file.name, file.type).message
                            }),
                            sourceArchiveName && /*#__PURE__*/ _jsxs("p", {
                                "data-testid": Testids.assistant.chatAttachmentSourceArchive,
                                // specra-allow text-px: matches the existing text-[11px] captions in this file
                                className: "text-[11px] text-muted-foreground truncate",
                                children: [
                                    "from ",
                                    sourceArchiveName
                                ]
                            }),
                            status && /*#__PURE__*/ _jsx("div", {
                                className: "text-[11px] text-muted-foreground mt-0.5",
                                children: statusLabel ? `${statusLabel}${status === 'uploading' && typeof progress === 'number' ? ` ${Math.round(progress)}%` : ''}` : status === 'uploading' ? `Uploading${typeof progress === 'number' ? ` ${Math.round(progress)}%` : ''}…` : status === 'expanding' ? 'Unpacking…' : status === 'uploaded' ? 'Uploaded' : status === 'error' ? 'Upload failed' : 'Pending upload'
                            }),
                            typeof progress === 'number' && status === 'uploading' && /*#__PURE__*/ _jsx("div", {
                                className: "h-1.5 bg-muted rounded mt-1 w-40",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-1.5 bg-primary rounded",
                                    style: {
                                        width: `${Math.max(0, Math.min(progress, 100))}%`
                                    }
                                })
                            }),
                            status === 'pending' && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) && onToggleDeepProcessing && /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-1.5 mt-1.5 cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "checkbox",
                                        checked: useDeepProcessing,
                                        onChange: onToggleDeepProcessing,
                                        className: "w-3 h-3 rounded border-muted-foreground/30 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer",
                                        disabled: disabled
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-[11px] text-muted-foreground select-none",
                                        children: "Deep processing (slower, better for scanned PDFs)"
                                    })
                                ]
                            }),
                            status === 'pending' && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) && useDeepProcessing && onOutputFormatChange && /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1.5 mt-1",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-[11px] text-muted-foreground select-none",
                                        children: "Output:"
                                    }),
                                    /*#__PURE__*/ _jsxs("select", {
                                        value: outputFormat || 'markdown',
                                        onChange: (e)=>onOutputFormatChange(e.target.value),
                                        className: "text-[11px] bg-transparent border border-muted-foreground/20 rounded px-1 py-0.5 text-muted-foreground",
                                        disabled: disabled,
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "markdown",
                                                children: "Markdown"
                                            }),
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "json",
                                                children: "JSON"
                                            }),
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "html",
                                                children: "HTML"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            unreadableArchiveChild && /*#__PURE__*/ _jsx("p", {
                                "data-testid": Testids.assistant.chatAttachmentUnreadableChild,
                                // specra-allow text-px: matches the existing text-[11px] captions in this file
                                className: "text-[11px] text-muted-foreground mt-1",
                                children: "Chat can't read this file, but it is attached and can still be filed."
                            }),
                            !unreadableArchiveChild && status === 'uploaded' && jobId && processingStatus && processingStatus !== 'none' && /*#__PURE__*/ _jsx("div", {
                                className: "mt-2",
                                children: /*#__PURE__*/ _jsx(JobStatus, {
                                    jobId: jobId,
                                    enabled: true,
                                    showCancel: false,
                                    onComplete: ()=>onJobTerminal?.(jobId, 'completed'),
                                    onError: ()=>onJobTerminal?.(jobId, 'failed'),
                                    onCancel: ()=>onJobTerminal?.(jobId, 'cancelled')
                                })
                            }),
                            status === 'error' && !jobId && /*#__PURE__*/ _jsx("p", {
                                "data-testid": "chat-attachment-upload-error",
                                className: "text-[11px] text-red-600 dark:text-red-400 mt-1",
                                children: rejection ? `${rejection} Remove it to send.` : 'Upload failed. Click retry, or remove and try a different file.'
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1 flex-shrink-0",
                children: [
                    canRetry && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "icon",
                        className: "h-7 w-7",
                        onClick: onRetry,
                        "aria-label": "Retry upload",
                        "data-testid": "chat-attachment-retry",
                        children: /*#__PURE__*/ _jsx(RotateCw, {
                            className: "w-4 h-4 text-muted-foreground hover:text-primary transition-colors"
                        })
                    }),
                    !disabled && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "icon",
                        className: "h-7 w-7",
                        onClick: onRemove,
                        "aria-label": "Remove file",
                        children: /*#__PURE__*/ _jsx(Trash2, {
                            className: "w-4 h-4 text-muted-foreground hover:text-red-500 transition-colors"
                        })
                    })
                ]
            })
        ]
    });
};
